<template>
    <div class="bw">
        <header class="bw-header">
            <div class="bw-header-inner">
                <h1>
                    <i class="fa fa-cubes"></i>
                    Bundle Workshop
                </h1>
                <p class="bw-subtitle">
                    Proposed AI starting bundles, mined from existing items.
                    Review each: approve, reject, edit, or note what needs changing.
                </p>
                <div class="bw-stats" v-if="loaded">
                    <span class="bw-stats-progress">
                        <strong>{{ reviewedCount }}</strong> of {{ proposals.length }} reviewed
                    </span>
                    <span><strong>{{ counts.approved }}</strong> approved</span>
                    <span><strong>{{ counts.needs_work }}</strong> needs work</span>
                    <span><strong>{{ counts.rejected }}</strong> rejected</span>
                    <span class="bw-stats-pending" v-if="counts.pending > 0">{{ counts.pending }} pending</span>
                </div>
                <div class="bw-actions">
                    <button class="bw-btn bw-btn-ghost" :disabled="loading" @click="regenerate">
                        <i class="fa fa-refresh"></i> Regenerate from latest data
                    </button>
                    <button
                        class="bw-btn bw-btn-primary"
                        :disabled="applying || pendingApplyCount === 0"
                        @click="applyApproved"
                        :title="pendingApplyCount === 0 ? 'No pending changes' : applyButtonTitle"
                    >
                        <i v-if="applying" class="fa fa-spinner fa-spin"></i>
                        <i v-else class="fa fa-rocket"></i>
                        <span v-if="pendingApplyCount > 0">Apply {{ pendingApplyCount }} {{ pendingApplyCount === 1 ? 'change' : 'changes' }}</span>
                        <span v-else>No changes to apply</span>
                    </button>
                    <a class="bw-btn bw-btn-ghost" :href="exportUrl" target="_blank">
                        <i class="fa fa-download"></i> Export JSON
                    </a>
                </div>
                <div v-if="applyMessage" class="bw-apply-msg" :class="{ 'is-error': applyMessage.error }">
                    {{ applyMessage.text }}
                </div>
            </div>
        </header>

        <main class="bw-main">
            <div v-if="loading" class="bw-loading">
                <i class="fa fa-spinner fa-spin"></i> Loading proposals…
            </div>

            <div v-else-if="error" class="bw-error">{{ error }}</div>

            <div v-else>
                <div class="bw-filter">
                    <label>
                        <input type="checkbox" v-model="hideRejected"> Hide rejected
                    </label>
                    <label>
                        <input type="checkbox" v-model="hideApproved"> Hide approved
                    </label>
                    <label>
                        Status:
                        <select v-model="statusFilter">
                            <option value="">all</option>
                            <option value="pending">pending</option>
                            <option value="approved">approved</option>
                            <option value="rejected">rejected</option>
                            <option value="needs_work">needs work</option>
                        </select>
                    </label>
                </div>

                <div v-if="visibleProposals.length === 0" class="bw-empty">
                    Nothing to show. Adjust filters or regenerate from data.
                </div>

                <div v-for="p in visibleProposals" :key="p.id" class="bw-card" :class="'bw-status-' + p.status">
                    <div class="bw-card-head">
                        <input
                            type="text"
                            v-model="p.suggested_name"
                            class="bw-card-name"
                            @change="save(p, ['suggested_name'])"
                        />
                        <div class="bw-card-meta">
                            <span class="bw-meta-tag">{{ p.keyword }}</span>
                            <span>· based on <strong>{{ p.source_item_count }}</strong> items</span>
                            <span v-if="p.avg_price_pence">· avg £{{ formatPrice(p.avg_price_pence) }}</span>
                            <span v-if="p.template_id" class="bw-meta-applied">
                                <i class="fa fa-rocket"></i>
                                Live as template #{{ p.template_id }}<span v-if="hasUnappliedEdits(p)"> · unapplied edits</span>
                            </span>
                        </div>
                    </div>

                    <div class="bw-card-options">
                        <div v-for="(value, key) in p.edited_options" :key="key" class="bw-option">
                            <span class="bw-option-key">{{ humanKey(key) }}</span>
                            <input
                                type="text"
                                v-model="p.edited_options[key]"
                                class="bw-option-value"
                                @change="save(p, ['edited_options'])"
                            />
                            <span v-if="p.confidence && p.confidence[key]" class="bw-option-conf"
                                  :title="confidenceTitle(p.confidence[key])">
                                {{ p.confidence[key].share }}%
                            </span>
                        </div>
                    </div>

                    <div class="bw-card-notes">
                        <textarea
                            v-model="p.notes"
                            placeholder="Notes for the AI prompt — when to use this bundle, what's typical, what to confirm with the customer…"
                            rows="2"
                            @change="save(p, ['notes'])"
                        ></textarea>
                    </div>

                    <div class="bw-card-actions">
                        <button
                            class="bw-btn bw-btn-success"
                            :class="{ 'is-active': p.status === 'approved' }"
                            @click="setStatus(p, 'approved')"
                        ><i class="fa fa-check"></i> Approve</button>
                        <button
                            class="bw-btn bw-btn-warning"
                            :class="{ 'is-active': p.status === 'needs_work' }"
                            @click="setStatus(p, 'needs_work')"
                        ><i class="fa fa-pencil"></i> Needs work</button>
                        <button
                            class="bw-btn bw-btn-danger"
                            :class="{ 'is-active': p.status === 'rejected' }"
                            @click="setStatus(p, 'rejected')"
                        ><i class="fa fa-times"></i> Reject</button>
                        <span class="bw-saved" v-if="lastSavedId === p.id">Saved.</span>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
export default {
    data: () => ({
        loaded: false,
        loading: true,
        error: null,
        proposals: [],
        generatedAt: null,
        monthsAnalysed: null,
        statusFilter: '',
        hideRejected: false,
        hideApproved: false,
        lastSavedId: null,
        applying: false,
        applyMessage: null,
    }),

    computed: {
        counts() {
            const c = { pending: 0, approved: 0, rejected: 0, needs_work: 0 };
            this.proposals.forEach(p => { c[p.status] = (c[p.status] || 0) + 1; });
            return c;
        },
        visibleProposals() {
            return this.proposals.filter(p => {
                if (this.hideRejected && p.status === 'rejected') return false;
                if (this.hideApproved && p.status === 'approved') return false;
                if (this.statusFilter && p.status !== this.statusFilter) return false;
                return true;
            });
        },
        exportUrl() {
            return '/settings/bundle-workshop/export';
        },

        // How many proposals have been reviewed (anything that isn't pending).
        reviewedCount() {
            return this.proposals.filter(p => p.status !== 'pending').length;
        },

        // Pending changes that the Apply button will process: approved
        // proposals needing publish (no template_id or has edits since
        // last apply) + rejected proposals with a live template that
        // should be deleted.
        pendingApplyCount() {
            return this.proposals.filter(p => {
                if (p.status === 'approved') return !p.template_id || this.hasUnappliedEdits(p);
                if (p.status === 'rejected') return !!p.template_id;
                return false;
            }).length;
        },

        applyButtonTitle() {
            const toPublish   = this.proposals.filter(p => p.status === 'approved' && (!p.template_id || this.hasUnappliedEdits(p))).length;
            const toUnpublish = this.proposals.filter(p => p.status === 'rejected' && !!p.template_id).length;
            const parts = [];
            if (toPublish > 0)   parts.push(`${toPublish} to publish`);
            if (toUnpublish > 0) parts.push(`${toUnpublish} to unpublish`);
            return parts.join(', ');
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        load(regenerate = false) {
            this.loading = true;
            this.error = null;
            window.axios.get('/settings/bundle-workshop/state', {
                params: regenerate ? { regenerate: 1 } : {},
            }).then(res => {
                this.proposals = res.data.proposals || [];
                this.generatedAt = res.data.generated_at;
                this.monthsAnalysed = res.data.months_analysed;
                this.loaded = true;
            }).catch(e => {
                this.error = (e && e.message) || 'Failed to load proposals.';
            }).then(() => { this.loading = false; });
        },
        regenerate() {
            if (!confirm('Regenerate proposals from latest data? Your status changes and edits will be lost unless backed up.')) return;
            this.load(true);
        },
        setStatus(p, status) {
            p.status = status;
            this.save(p, ['status']);
        },
        save(p, fields) {
            const payload = {};
            fields.forEach(f => { payload[f] = p[f]; });
            window.axios.put(`/settings/bundle-workshop/proposals/${p.id}`, payload)
                .then(() => {
                    this.lastSavedId = p.id;
                    setTimeout(() => {
                        if (this.lastSavedId === p.id) this.lastSavedId = null;
                    }, 1500);
                })
                .catch(e => {
                    this.error = 'Save failed: ' + ((e && e.message) || 'unknown');
                });
        },
        humanKey(key) {
            return key.replace(/_/g, ' ').replace(/\bmm\b/, 'mm');
        },
        formatPrice(pence) {
            return Math.round(pence / 100).toLocaleString('en-GB');
        },
        confidenceTitle(c) {
            const runners = (c.runners_up || []).join(', ');
            return `${c.top_count} of ${c.denominator} items (${c.share}%). ` +
                (runners ? `Runners-up: ${runners}` : 'No other contenders.');
        },

        // True if the proposal has been applied before but its current
        // values differ from when it was last applied. Cheap heuristic:
        // applied_at timestamp older than updated_at means edits since.
        hasUnappliedEdits(p) {
            if (!p.template_id) return false;
            if (!p.applied_at) return true;
            if (!p.updated_at) return false;
            return new Date(p.updated_at).getTime() > new Date(p.applied_at).getTime();
        },

        applyApproved() {
            if (this.applying) return;
            if (!confirm('Apply approved proposals as live AI bundles? This will create or update template items the AI can immediately use.')) return;
            this.applying = true;
            this.applyMessage = null;
            window.axios.post('/settings/bundle-workshop/apply', {})
                .then(res => {
                    const d = res.data;
                    if (d.errors && d.errors.length) {
                        this.applyMessage = { error: true, text: `Applied ${d.applied}, but ${d.errors.length} failed: ` + d.errors.map(e => e.name + ' (' + e.message + ')').join('; ') };
                    } else {
                        this.applyMessage = { error: false, text: d.note };
                    }
                    // Reload to get template_id stamps + applied_at timestamps.
                    this.load(false);
                })
                .catch(e => {
                    this.applyMessage = { error: true, text: 'Apply failed: ' + ((e && e.message) || 'unknown') };
                })
                .then(() => { this.applying = false; });
        },
    },
};
</script>

<style>
.bw {
    --bw-bg: #f7f8fa;
    --bw-card: #fff;
    --bw-text: #1f2937;
    --bw-muted: #6b7280;
    --bw-border: #e5e7eb;
    --bw-primary: #2563eb;
    --bw-primary-hover: #1d4ed8;
    --bw-success: #059669;
    --bw-warning: #d97706;
    --bw-danger: #dc2626;
    --bw-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 4px 12px rgba(15, 23, 42, .06);

    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: var(--bw-text);
    background: var(--bw-bg);
    padding: 24px 16px 60px;
    min-height: calc(100vh - 60px);
}

.bw *, .bw *::before, .bw *::after { box-sizing: border-box; }

.bw-header-inner, .bw-main {
    max-width: 1100px;
    margin: 0 auto;
}

.bw-header {
    margin-bottom: 24px;
}

.bw-header h1 {
    font-size: 26px;
    font-weight: 600;
    margin: 0 0 6px;
}

.bw-header h1 .fa {
    color: var(--bw-primary);
    margin-right: 6px;
}

.bw-subtitle {
    color: var(--bw-muted);
    margin: 0 0 14px;
    font-size: 14px;
}

.bw-stats {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 13px;
    color: var(--bw-muted);
    margin-bottom: 14px;
}

.bw-stats strong { color: var(--bw-text); font-weight: 600; }

.bw-stats-pending { color: var(--bw-warning); }

.bw-stats-progress {
    background: var(--bw-primary);
    color: #fff;
    padding: 2px 10px;
    border-radius: 999px;
}

.bw-stats-progress strong { color: #fff; }

.bw-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.bw-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--bw-border);
    background: #fff;
    border-radius: 8px;
    padding: 8px 14px;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--bw-text);
    cursor: pointer;
    text-decoration: none;
    transition: background-color .12s, border-color .12s;
}

.bw-btn:hover { background: #f3f4f6; }
.bw-btn:disabled { opacity: .5; cursor: not-allowed; }

.bw-btn-primary {
    background: var(--bw-primary); color: #fff; border-color: var(--bw-primary);
}
.bw-btn-primary:hover { background: var(--bw-primary-hover); border-color: var(--bw-primary-hover); }
.bw-btn-ghost { background: transparent; }
.bw-btn-success { color: var(--bw-success); }
.bw-btn-success.is-active { background: var(--bw-success); color: #fff; border-color: var(--bw-success); }
.bw-btn-warning { color: var(--bw-warning); }
.bw-btn-warning.is-active { background: var(--bw-warning); color: #fff; border-color: var(--bw-warning); }
.bw-btn-danger { color: var(--bw-danger); }
.bw-btn-danger.is-active { background: var(--bw-danger); color: #fff; border-color: var(--bw-danger); }

.bw-loading, .bw-empty, .bw-error {
    text-align: center;
    padding: 60px 20px;
    color: var(--bw-muted);
}
.bw-error { color: var(--bw-danger); }

.bw-filter {
    display: flex;
    gap: 16px;
    align-items: center;
    background: #fff;
    border: 1px solid var(--bw-border);
    border-radius: 8px;
    padding: 8px 14px;
    margin-bottom: 14px;
    font-size: 13px;
    color: var(--bw-muted);
}

.bw-filter label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-weight: 400;
}

.bw-filter select { padding: 2px 6px; }

.bw-card {
    background: var(--bw-card);
    border: 1px solid var(--bw-border);
    border-left-width: 4px;
    border-radius: 10px;
    box-shadow: var(--bw-shadow);
    padding: 16px 18px;
    margin-bottom: 14px;
}

.bw-status-pending     { border-left-color: var(--bw-muted); }
.bw-status-approved    { border-left-color: var(--bw-success); background: #f0fdf4; }
.bw-status-needs_work  { border-left-color: var(--bw-warning); background: #fffbeb; }
.bw-status-rejected    { border-left-color: var(--bw-danger); opacity: 0.65; }

.bw-card-head { margin-bottom: 12px; }

.bw-card-name {
    width: 100%;
    border: 1px solid transparent;
    background: transparent;
    font: inherit;
    font-size: 17px;
    font-weight: 600;
    padding: 4px 6px;
    border-radius: 4px;
    color: var(--bw-text);
}

.bw-card-name:hover, .bw-card-name:focus {
    outline: none;
    background: #fff;
    border-color: var(--bw-border);
}

.bw-card-meta {
    margin-top: 4px;
    font-size: 12px;
    color: var(--bw-muted);
    padding-left: 6px;
}

.bw-meta-tag {
    background: #eff6ff;
    color: var(--bw-primary);
    padding: 1px 8px;
    border-radius: 999px;
    font-weight: 500;
    text-transform: lowercase;
    margin-right: 4px;
}

.bw-meta-applied {
    background: #ede9fe;
    color: #6d28d9;
    padding: 1px 8px;
    border-radius: 999px;
    font-weight: 500;
    margin-left: 6px;
    font-size: 11px;
}

.bw-apply-msg {
    margin-top: 10px;
    padding: 8px 12px;
    background: #ecfdf5;
    color: #065f46;
    border-radius: 6px;
    font-size: 13px;
}

.bw-apply-msg.is-error {
    background: #fef2f2;
    color: #b91c1c;
}

.bw-card-options {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 6px 12px;
    padding: 12px 0;
    border-top: 1px dashed var(--bw-border);
    border-bottom: 1px dashed var(--bw-border);
}

.bw-option {
    display: grid;
    grid-template-columns: 130px 1fr auto;
    gap: 6px;
    align-items: center;
    font-size: 12.5px;
}

.bw-option-key {
    color: var(--bw-muted);
    font-weight: 500;
}

.bw-option-value {
    border: 1px solid var(--bw-border);
    border-radius: 4px;
    padding: 3px 7px;
    font: inherit;
    font-size: 12.5px;
    background: #fff;
    color: var(--bw-text);
    width: 100%;
}

.bw-option-value:focus {
    outline: none;
    border-color: var(--bw-primary);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, .15);
}

.bw-option-conf {
    color: var(--bw-muted);
    font-size: 11px;
    cursor: help;
    text-align: right;
    min-width: 40px;
}

.bw-card-notes {
    margin-top: 12px;
}

.bw-card-notes textarea {
    width: 100%;
    border: 1px solid var(--bw-border);
    border-radius: 6px;
    padding: 6px 9px;
    font: inherit;
    font-size: 13px;
    color: var(--bw-text);
    resize: vertical;
}

.bw-card-notes textarea:focus {
    outline: none;
    border-color: var(--bw-primary);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, .15);
}

.bw-card-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
}

.bw-saved {
    color: var(--bw-success);
    font-size: 12px;
    margin-left: auto;
    font-style: italic;
}
</style>
